import React, {useState, useEffect, useMemo, useRef, useCallback} from "react";
import {db} from "../config/firebase-config";
import {
  collection,
  where,
  getCountFromServer,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
} from "firebase/firestore";
import {CSVLink} from "react-csv";
import {datumsfilterGrenzen} from "../utils/protokoll";

import Card from "react-bootstrap/Card";
import {Container} from "react-bootstrap";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

const protokollRef = collection(db, "protokoll");

const getDefaultFilters = () => {
  return {
    fahrzeug: "",
    kennung: "",
    fromDate: "",
    toDate: "",
  };
};

const Protokoll = ({
  fahrzeuge,
  funkkennungen,
  searchString,
  setSearchString,
}) => {
  const PAGE_SIZE = 250;

  const [protokoll, setProtokoll] = useState([]);
  const [protokollCSV, setProtokollCSV] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [totalEntries, setTotalEntries] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilters, setActiveFilters] = useState(getDefaultFilters);
  const [draftFilters, setDraftFilters] = useState(getDefaultFilters);
  const [searchText, setSearchText] = useState("");

  const pageCursorsRef = useRef({1: null});

  const csvHeaders = [
    {label: "Datum, Uhrzeit", key: "datum"},
    {label: "Fahrzeug", key: "fahrzeug"},
    {label: "Status", key: "status"},
    {label: "Kennung Aktuell", key: "kennung"},
    {label: "Kennung Vorher", key: "kennungOld"},
    {label: "Standort", key: "standort"},
    {label: "Bemerkung", key: "bemerkung"},
  ];

  const csvFilename = "Fahrzeugverwaltung_Protokoll.csv";

  const normalizeText = (value) => {
    if (value === null || value === undefined) {
      return "";
    }

    return String(value).toLowerCase();
  };

  const buildFilterConstraints = useCallback((filters) => {
    const constraints = [];

    if (filters.fahrzeug.trim() !== "") {
      constraints.push(where("fahrzeug", "==", filters.fahrzeug.trim()));
    }

    if (filters.kennung.trim() !== "") {
      constraints.push(where("kennung", "==", filters.kennung.trim()));
    }

    const {von, bis} = datumsfilterGrenzen(filters);

    if (von !== null) {
      constraints.push(where("timestamp", ">=", von));
    }

    if (bis !== null) {
      constraints.push(where("timestamp", "<=", bis));
    }

    return constraints;
  }, []);

  const loadTotalEntries = useCallback(
    async (filters) => {
      const constraints = buildFilterConstraints(filters);
      const countQuery = query(protokollRef, ...constraints);
      const countSnapshot = await getCountFromServer(countQuery);
      const count = countSnapshot.data().count;

      setTotalEntries(count);
      return count;
    },
    [buildFilterConstraints],
  );

  const loadPage = useCallback(
    async (page, filters) => {
      const pageStartCursor = pageCursorsRef.current[page];

      if (page > 1 && pageStartCursor === undefined) {
        return;
      }

      setIsLoading(true);
      setError("");

      try {
        const constraints = buildFilterConstraints(filters);
        const baseConstraints = [
          ...constraints,
          orderBy("timestamp", "desc"),
          limit(PAGE_SIZE),
        ];

        const pageQuery = pageStartCursor
          ? query(
              protokollRef,
              ...constraints,
              orderBy("timestamp", "desc"),
              startAfter(pageStartCursor),
              limit(PAGE_SIZE),
            )
          : query(protokollRef, ...baseConstraints);

        const snapshot = await getDocs(pageQuery);
        const protokollTmp = [];

        snapshot.forEach((snapshotDoc) => {
          protokollTmp.push({...snapshotDoc.data(), id: snapshotDoc.id});
        });

        const docs = snapshot.docs;
        const newCursorMap = {
          ...pageCursorsRef.current,
          [page]: pageStartCursor || null,
        };

        if (docs.length > 0) {
          newCursorMap[page + 1] = docs[docs.length - 1];
        }

        pageCursorsRef.current = newCursorMap;
        setProtokoll(protokollTmp);
        setCurrentPage(page);
      } catch (loadError) {
        if (loadError && loadError.code === "failed-precondition") {
          setError(
            "Für diese Filter fehlt ein Firestore-Index. Bitte Index anlegen und erneut laden.",
          );
        } else {
          setError("Protokoll konnte nicht geladen werden.");
        }
      } finally {
        setIsLoading(false);
      }
    },
    [buildFilterConstraints],
  );

  const protokollFilter = useMemo(() => {
    if (searchText.trim() === "") {
      return protokoll;
    }

    const normalizedSearch = searchText.toLowerCase();

    return protokoll.filter((item) => {
      return (
        normalizeText(item.kennung).includes(normalizedSearch) ||
        normalizeText(item.kennungAlt).includes(normalizedSearch) ||
        normalizeText(item.fahrzeug).includes(normalizedSearch) ||
        normalizeText(item.bemerkung).includes(normalizedSearch) ||
        normalizeText(item.standort).includes(normalizedSearch)
      );
    });
  }, [protokoll, searchText]);

  const fahrzeugOptions = useMemo(() => {
    const values = new Set();

    fahrzeuge.forEach((item) => {
      if (item.kennzeichen && item.kennzeichen.trim() !== "") {
        values.add(item.kennzeichen);
      }
    });

    return Array.from(values).sort((a, b) => a.localeCompare(b, "de"));
  }, [fahrzeuge]);

  const kennungOptions = useMemo(() => {
    const values = new Set();

    funkkennungen.forEach((item) => {
      if (item.bezeichnung && item.bezeichnung.trim() !== "") {
        values.add(item.bezeichnung);
      }
    });

    return Array.from(values).sort((a, b) => a.localeCompare(b, "de"));
  }, [funkkennungen]);

  useEffect(() => {
    let tmpArray = [];

    protokollFilter.forEach((item) => {
      const dateProtokoll = item.datum.replace("->", "") + " Uhr";

      tmpArray.push({
        datum: dateProtokoll,
        fahrzeug: item.fahrzeug,
        status: statusToText(item.status),
        kennung: item.kennung,
        kennungOld: item.kennungAlt,
        standort: item.standort,
        bemerkung: item.bemerkung,
      });
    });
    setProtokollCSV(tmpArray);
  }, [protokollFilter]);

  useEffect(() => {
    setSearchString("");
  }, [setSearchString]);

  useEffect(() => {
    setSearchText(searchString || "");
  }, [searchString]);

  useEffect(() => {
    const loadInitialPage = async () => {
      setIsLoading(true);
      setError("");
      pageCursorsRef.current = {1: null};
      setCurrentPage(1);

      try {
        const count = await loadTotalEntries(activeFilters);

        if (count === 0) {
          setProtokoll([]);
          return;
        }

        await loadPage(1, activeFilters);
      } catch (loadError) {
        setProtokoll([]);
        if (loadError && loadError.code === "failed-precondition") {
          setError(
            "Für diese Filter fehlt ein Firestore-Index. Bitte Index anlegen und erneut laden.",
          );
        } else {
          setError("Protokoll konnte nicht geladen werden.");
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialPage();
  }, [activeFilters, loadTotalEntries, loadPage]);

  const updateDraftFilter = (key, value) => {
    setDraftFilters((prevFilters) => {
      return {
        ...prevFilters,
        [key]: value,
      };
    });
  };

  const applyFilters = () => {
    setActiveFilters({...draftFilters});
  };

  const resetFilters = () => {
    const resetFilterSet = getDefaultFilters();
    setDraftFilters(resetFilterSet);
    setActiveFilters(resetFilterSet);
  };

  const totalPages = Math.max(1, Math.ceil(totalEntries / PAGE_SIZE));

  const goToNextPage = async () => {
    if (isLoading || currentPage >= totalPages) {
      return;
    }

    await loadPage(currentPage + 1, activeFilters);
  };

  const goToPreviousPage = async () => {
    if (isLoading || currentPage <= 1) {
      return;
    }

    await loadPage(currentPage - 1, activeFilters);
  };

  const statusToText = (status) => {
    let result = "";

    switch (status) {
      case "aussserdienst":
      case "ausserdienst":
        result = "Nicht Dienstfähig";
        break;
      case "einsatzbereit":
        result = "Regelbedarf";
        break;
      case "desinfektion":
        result = "Desinfektion";
        break;
      case "technischeReserve":
        result = "Reservefahrzeuge";
        break;
      case "taktischeReserve":
        result = "Spitzenbedarf";
        break;
      case "sonderbedarf":
        result = "Sonderbedarf";
        break;
      case "fuehrung":
        result = "Führung";
        break;
      case "fahrauftrag":
        result = "Fahrauftrag";
        break;
      default:
        result = "Fehler";
    }

    return result;
  };

  return (
    <Container>
      <Card className="statusColumn">
        <Card.Header style={{backgroundColor: "lightgray"}}>
          <div className="settingHeader">
            <div className="settingTitle">Protokoll</div>
            <div>
              <CSVLink
                data={protokollCSV}
                headers={csvHeaders}
                filename={csvFilename}
              >
                <Button
                  variant="primary"
                  className="btn-header"
                  onClick={() => {}}
                >
                  CSV Export
                </Button>
              </CSVLink>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <Form style={{marginBottom: "12px"}}>
            <Row
              className="g-2 align-items-center"
              style={{marginBottom: "8px"}}
            >
              <Col md={3}>
                <Form.Select
                  value={draftFilters.fahrzeug}
                  onChange={(event) => {
                    updateDraftFilter("fahrzeug", event.target.value);
                  }}
                >
                  <option value="">Fahrzeug: alle</option>
                  {fahrzeugOptions.map((fahrzeug) => {
                    return (
                      <option key={fahrzeug} value={fahrzeug}>
                        {fahrzeug}
                      </option>
                    );
                  })}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={draftFilters.kennung}
                  onChange={(event) => {
                    updateDraftFilter("kennung", event.target.value);
                  }}
                >
                  <option value="">Kennung: alle</option>
                  {kennungOptions.map((kennung) => {
                    return (
                      <option key={kennung} value={kennung}>
                        {kennung}
                      </option>
                    );
                  })}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Control
                  type="date"
                  value={draftFilters.fromDate}
                  onChange={(event) => {
                    updateDraftFilter("fromDate", event.target.value);
                  }}
                />
              </Col>
              <Col md={2}>
                <Form.Control
                  type="date"
                  value={draftFilters.toDate}
                  onChange={(event) => {
                    updateDraftFilter("toDate", event.target.value);
                  }}
                />
              </Col>
              <Col md={2}>
                <Button
                  variant="primary"
                  className="btn-header"
                  onClick={applyFilters}
                  disabled={isLoading}
                >
                  Filter anwenden
                </Button>
                <Button
                  variant="secondary"
                  className="btn-header"
                  onClick={resetFilters}
                  disabled={isLoading}
                >
                  X
                </Button>
              </Col>
            </Row>
          </Form>

          {error !== "" ? <div className="loginError">{error}</div> : undefined}

          <Table striped bordered hover className="protokollTable">
            <thead>
              <tr>
                <th className="col-2">Datum, Uhrzeit</th>
                <th className="col-1">Fahrzeug</th>
                <th className="col-2">Status</th>
                <th className="col-2">Kennung</th>
                <th className="col-1">Standort</th>
                <th className="col-4">Bemerkung</th>
              </tr>
            </thead>
            <tbody>
              {protokollFilter.map((item, index) => {
                return (
                  <tr key={index}>
                    <td>{item.datum.replace("->", "")} Uhr</td>
                    <td>{item.fahrzeug}</td>
                    <td>{statusToText(item.status)}</td>
                    <td>
                      Aktuell: {item.kennung === "" ? <>Ohne</> : item.kennung}
                      {"kennungAlt" in item ? (
                        <>
                          <br />
                          <span className="protokollAlt">
                            Vorher:{" "}
                            {item.kennungAlt === "" ? (
                              <>Ohne</>
                            ) : (
                              item.kennungAlt
                            )}
                          </span>{" "}
                        </>
                      ) : undefined}
                    </td>
                    <td>{item.standort}</td>
                    <td>{item.bemerkung}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
        <Card.Footer className="statusFooter">
          <div>
            {isLoading ? "Lade Daten ..." : undefined}
            {!isLoading && protokollFilter.length === 0
              ? "Keine Einträge"
              : undefined}
            {!isLoading && protokollFilter.length === 1
              ? "1 Eintrag auf dieser Seite"
              : undefined}
            {!isLoading && protokollFilter.length > 1
              ? protokollFilter.length + " Einträge auf dieser Seite"
              : undefined}
          </div>
          <div style={{marginTop: "8px"}}>
            <Button
              variant="secondary"
              className="btn-header"
              onClick={goToPreviousPage}
              disabled={isLoading || currentPage <= 1}
            >
              Vorherige Seite
            </Button>
            <Button
              variant="secondary"
              className="btn-header"
              onClick={goToNextPage}
              disabled={isLoading || currentPage >= totalPages}
            >
              Nächste Seite
            </Button>
            <span style={{marginLeft: "8px"}}>
              Seite {currentPage} von {totalPages} ({totalEntries} Einträge
              gesamt)
            </span>
          </div>
        </Card.Footer>
      </Card>
    </Container>
  );
};

export default Protokoll;
